import { supabase } from '../supabaseClient';
import { resolveForfeoReserveEdgeErrorCode } from './forfeoReserveEdgeError';
import {
  FORFEO_RESERVE_MERCHANT_PORTAL_SCHEMA,
  validateForfeoReserveMerchantPortalCommand,
  validateForfeoReserveMerchantPortalExportRequest,
  validateForfeoReserveMerchantPortalWindowRequest,
  type ForfeoReserveMerchantPortalCommand,
  type ForfeoReserveMerchantPortalExportKind,
  type ForfeoReserveMerchantPortalSnapshot,
  type ForfeoReserveMerchantPortalWindowRequest,
} from '../../supabase/functions/_shared/forfeo-reserve-merchant-portal';

export const FORFEO_RESERVE_MERCHANT_PORTAL_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_MERCHANT_PORTAL_V1' as const;

export function isForfeoReserveMerchantPortalEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_MERCHANT_PORTAL_V1 === 'true';
}

export class ForfeoReserveMerchantPortalClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReserveMerchantPortalClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve-merchant-portal:${crypto.randomUUID()}`;
}

function idempotencyKey(kind: string): string {
  return `reserve:merchant-portal:${kind}:${crypto.randomUUID()}`;
}

async function invokePortal<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(
    'reserve-merchant-portal',
    { body },
  );
  if (error) {
    throw new ForfeoReserveMerchantPortalClientError(
      await resolveForfeoReserveEdgeErrorCode(
        data,
        error,
        'portal_network_error',
      ),
    );
  }
  if (!data?.ok) {
    throw new ForfeoReserveMerchantPortalClientError(
      String(data?.error || 'portal_operation_failed'),
    );
  }
  return data as T;
}

export function createForfeoReserveMerchantPortalWindow(
  input: Omit<ForfeoReserveMerchantPortalWindowRequest, 'correlationId'>
    & Partial<Pick<ForfeoReserveMerchantPortalWindowRequest, 'correlationId'>>,
): ForfeoReserveMerchantPortalWindowRequest {
  const request: ForfeoReserveMerchantPortalWindowRequest = {
    ...input,
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveMerchantPortalWindowRequest(request);
  if (errors.length > 0) {
    throw new ForfeoReserveMerchantPortalClientError(errors.join(','));
  }
  return request;
}

export function createForfeoReserveMerchantPortalCommand(
  input: Omit<ForfeoReserveMerchantPortalCommand, 'correlationId' | 'idempotencyKey'>
    & Partial<Pick<ForfeoReserveMerchantPortalCommand, 'correlationId' | 'idempotencyKey'>>,
): ForfeoReserveMerchantPortalCommand {
  const command: ForfeoReserveMerchantPortalCommand = {
    ...input,
    correlationId: input.correlationId || correlationId(),
    idempotencyKey: input.idempotencyKey || idempotencyKey(input.kind),
  };
  const errors = validateForfeoReserveMerchantPortalCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveMerchantPortalClientError(errors.join(','));
  }
  return command;
}

export async function fetchForfeoReserveMerchantPortalSnapshot(
  request: ForfeoReserveMerchantPortalWindowRequest,
): Promise<ForfeoReserveMerchantPortalSnapshot> {
  const errors = validateForfeoReserveMerchantPortalWindowRequest(request);
  if (errors.length > 0) {
    throw new ForfeoReserveMerchantPortalClientError(errors.join(','));
  }
  const data = await invokePortal<{
    ok: true;
    schema: typeof FORFEO_RESERVE_MERCHANT_PORTAL_SCHEMA;
    snapshot: ForfeoReserveMerchantPortalSnapshot;
  }>({ action: 'snapshot', request });
  return data.snapshot;
}

export async function applyForfeoReserveMerchantPortalCommand(
  command: ForfeoReserveMerchantPortalCommand,
): Promise<Record<string, unknown>> {
  const errors = validateForfeoReserveMerchantPortalCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveMerchantPortalClientError(errors.join(','));
  }
  const data = await invokePortal<{
    ok: true;
    result: Record<string, unknown>;
  }>({ action: 'command', command });
  return data.result;
}

export async function exportForfeoReserveMerchantPortal(
  input: ForfeoReserveMerchantPortalWindowRequest & {
    exportKind: ForfeoReserveMerchantPortalExportKind;
    rowLimit?: number;
  },
): Promise<{ filename: string; mimeType: string; content: string; rowCount: number }> {
  const errors = validateForfeoReserveMerchantPortalExportRequest(input);
  if (errors.length > 0) {
    throw new ForfeoReserveMerchantPortalClientError(errors.join(','));
  }
  const data = await invokePortal<{
    ok: true;
    export: { filename: string; mimeType: string; content: string; rowCount: number };
  }>({ action: 'export', request: input });
  return data.export;
}
